import { useState, useEffect, useCallback } from "react";
import { Loader2, CheckCircle2, XCircle, HelpCircle, GitBranch, ArrowRight, Sparkles, Brain, MinusCircle } from "lucide-react";
import { Button } from "../../ui/button";
import { toast } from "sonner";
import CreditCostTag from "../../CreditCostTag";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  validated: { icon: CheckCircle2, color: "text-emerald-400", bg: "border-emerald-500/30 bg-emerald-500/5", label: "Validated" },
  refuted: { icon: XCircle, color: "text-destructive", bg: "border-destructive/30 bg-destructive/5", label: "Refuted" },
  inconclusive: { icon: MinusCircle, color: "text-amber-400", bg: "border-amber-500/30 bg-amber-500/5", label: "Inconclusive" },
  untested: { icon: HelpCircle, color: "text-muted-foreground", bg: "border-border bg-muted/30", label: "Untested" },
};

const CONFIDENCE_BADGE = {
  high: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
  medium: "bg-amber-500/20 text-amber-400 border-amber-500/30",
  low: "bg-muted text-muted-foreground border-border",
};

export default function TraceabilityPanel({ projectId, token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [aiMatching, setAiMatching] = useState(false);
  const [filterStatus, setFilterStatus] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/projects/${projectId}/fishbone-traceability`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setData(await res.json());
    } catch (e) {
      console.error("Failed to load traceability:", e);
    } finally {
      setLoading(false);
    }
  }, [projectId, token]);

  useEffect(() => { load(); }, [load]);

  const runAiMatching = async () => {
    setAiMatching(true);
    try {
      const res = await fetch(`${API}/ai/semantic-traceability`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ project_id: projectId }),
      });
      if (!res.ok) throw new Error("AI matching failed");
      const result = await res.json();
      toast.success(`AI matched ${result.summary.matched} of ${result.summary.total_causes} causes`);
      await load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setAiMatching(false);
    }
  };

  if (loading && !data) {
    return (
      <div className="bg-card border border-border rounded-lg p-6 flex items-center justify-center py-8 mt-6">
        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!data || !data.traceability?.length) return null;

  const { traceability, summary, ai_enriched } = data;
  const filtered = filterStatus === "all" ? traceability : traceability.filter(t => t.status === filterStatus);
  const categories = [...new Set(traceability.map(t => t.category))];

  return (
    <div className="bg-card border border-border rounded-lg p-6 mt-6" data-testid="traceability-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <GitBranch className="w-5 h-5 text-primary" />
          <div>
            <h3 className="font-heading font-semibold text-sm">Fishbone-to-Analyze Traceability</h3>
            <p className="text-xs text-muted-foreground">Which hypothesized causes have statistical evidence?</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={runAiMatching}
            disabled={aiMatching}
            className="gap-1"
            data-testid="ai-match-btn"
          >
            {aiMatching ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Sparkles className="w-4 h-4" />
            )}
            {aiMatching ? "Matching..." : (ai_enriched ? "Re-run AI Match" : "AI Match")}
            <CreditCostTag cost={10} />
          </Button>
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            Refresh
          </Button>
        </div>
      </div>

      {ai_enriched && (
        <div className="flex items-center gap-2 mb-4 p-2 bg-primary/5 border border-primary/20 rounded-lg" data-testid="ai-enriched-badge">
          <Brain className="w-4 h-4 text-primary" />
          <span className="text-xs text-primary font-medium">AI-enhanced semantic matching active</span>
        </div>
      )}

      {/* Summary counters */}
      <div className="grid grid-cols-4 gap-3 mb-4">
        <button
          onClick={() => setFilterStatus("all")}
          className={`rounded-lg p-3 border text-left transition-colors ${filterStatus === "all" ? "border-primary bg-primary/10" : "border-border bg-background"}`}
          data-testid="filter-all"
        >
          <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Total</p>
          <p className="text-lg font-bold font-mono" data-testid="traceability-total">{summary.total_causes}</p>
        </button>
        <button
          onClick={() => setFilterStatus("validated")}
          className={`rounded-lg p-3 border text-left transition-colors ${filterStatus === "validated" ? "border-emerald-500 bg-emerald-500/10" : "border-border bg-background"}`}
          data-testid="filter-validated"
        >
          <p className="text-[10px] text-emerald-400 uppercase tracking-wider">Validated</p>
          <p className="text-lg font-bold font-mono text-emerald-400" data-testid="traceability-validated">{summary.validated}</p>
        </button>
        <button
          onClick={() => setFilterStatus("refuted")}
          className={`rounded-lg p-3 border text-left transition-colors ${filterStatus === "refuted" ? "border-destructive bg-destructive/10" : "border-border bg-background"}`}
          data-testid="filter-refuted"
        >
          <p className="text-[10px] text-destructive uppercase tracking-wider">Refuted</p>
          <p className="text-lg font-bold font-mono text-destructive" data-testid="traceability-refuted">{summary.refuted}</p>
        </button>
        <button
          onClick={() => setFilterStatus("untested")}
          className={`rounded-lg p-3 border text-left transition-colors ${filterStatus === "untested" ? "border-muted-foreground/50 bg-muted/30" : "border-border bg-background"}`}
          data-testid="filter-untested"
        >
          <p className="text-[10px] text-muted-foreground uppercase tracking-wider">Untested</p>
          <p className="text-lg font-bold font-mono" data-testid="traceability-untested">{summary.untested}</p>
        </button>
      </div>

      {/* Traceability by category */}
      <div className="space-y-4">
        {categories.map(cat => {
          const catItems = filtered.filter(t => t.category === cat);
          if (catItems.length === 0) return null;
          return (
            <div key={cat} data-testid={`traceability-category-${cat.toLowerCase().replace(/\s/g, '-')}`}>
              <h4 className="text-xs font-bold uppercase tracking-wider text-primary mb-2">{cat}</h4>
              <div className="space-y-1.5">
                {catItems.map((item, i) => {
                  const cfg = STATUS_CONFIG[item.status] || STATUS_CONFIG.untested;
                  const Icon = cfg.icon;
                  const aiMatch = item.ai_match;

                  return (
                    <div key={i} className={`flex items-start gap-3 p-2.5 rounded-lg border ${cfg.bg}`} data-testid={`traceability-item-${item.status}`}>
                      <Icon className={`w-4 h-4 ${cfg.color} shrink-0 mt-0.5`} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium">{item.cause}</p>

                        {/* Standard evidence (text matching or AI-upgraded) */}
                        {item.evidence && (
                          <div className="flex items-center gap-1.5 mt-1 text-[10px] text-muted-foreground">
                            <ArrowRight className="w-3 h-3" />
                            {item.evidence.source === "staged_rca" && (
                              <span>Matched to Staged RCA driver: <strong className="text-emerald-400">{item.evidence.driver}</strong></span>
                            )}
                            {item.evidence.source === "hypothesis_test" && (
                              <span>
                                {item.evidence.test_name} on column <strong>{item.evidence.column}</strong>
                                {item.evidence.p_value != null && ` (p = ${typeof item.evidence.p_value === 'number' ? item.evidence.p_value.toFixed(4) : item.evidence.p_value})`}
                                {item.evidence.significant ? " — Significant" : " — Not significant"}
                              </span>
                            )}
                            {item.evidence.source === "ai_semantic_match" && (
                              <span>
                                AI matched to <strong className="text-primary">{item.evidence.matched_element}</strong>
                                {item.evidence.confidence && (
                                  <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-[9px] border ${CONFIDENCE_BADGE[item.evidence.confidence] || CONFIDENCE_BADGE.low}`}>
                                    {item.evidence.confidence}
                                  </span>
                                )}
                              </span>
                            )}
                          </div>
                        )}

                        {/* AI match rationale (when available and no standard evidence) */}
                        {aiMatch && !item.evidence && aiMatch.element_type !== "no_match" && (
                          <div className="flex items-center gap-1.5 mt-1 text-[10px] text-muted-foreground">
                            <Brain className="w-3 h-3 text-primary" />
                            <span>
                              AI suggests: <strong className="text-primary">{aiMatch.matched_element}</strong>
                              {aiMatch.confidence && (
                                <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-[9px] border ${CONFIDENCE_BADGE[aiMatch.confidence] || CONFIDENCE_BADGE.low}`}>
                                  {aiMatch.confidence}
                                </span>
                              )}
                            </span>
                          </div>
                        )}

                        {/* AI rationale tooltip */}
                        {aiMatch?.rationale && (
                          <p className="text-[10px] text-muted-foreground/70 mt-0.5 italic">{aiMatch.rationale}</p>
                        )}
                      </div>
                      <span className={`text-[10px] font-medium ${cfg.color} shrink-0`}>{cfg.label}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
